import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { Product } from "@/types/product";
import { productService } from "@/services/product.service";
import {
  productCategoryService,
  ProductCategory as ApiProductCategory,
} from "@/services/product-category.service";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface LocalProductCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

interface ProductsContextType {
  products: Product[];
  categories: LocalProductCategory[];
  rules: any[];
  isLoading: boolean;
  categoriesLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  slug: string;
  organizationName: string;
  fetchProducts: (params?: any) => Promise<void>;
  fetchCategories: () => Promise<void>;
  fetchRules: () => Promise<void>;
  createRule: (ruleData: any) => Promise<boolean>;
  updateRule: (id: string, ruleData: any) => Promise<boolean>;
  deleteRule: (id: string, productId: string) => Promise<boolean>;
  getProductById: (id: string) => Promise<Product | null>;
  createProduct: (productData: any) => Promise<Product | null>;
  updateProduct: (id: string, productData: any) => Promise<Product | null>;
  deleteProduct: (id: string) => Promise<boolean>;
  createCategory: (categoryData: any) => Promise<LocalProductCategory | null>;
  updateCategory: (
    id: string,
    categoryData: any,
  ) => Promise<LocalProductCategory | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  setCurrentPage: (page: number) => void;
}

const ProductsContext = createContext<ProductsContextType | undefined>(
  undefined,
);

interface ProductsProviderProps {
  children: ReactNode;
  slug?: string;
}

export function ProductsProvider({ children, slug: propSlug }: ProductsProviderProps) {
  const { toast } = useToast();
  const { user, company } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<LocalProductCategory[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (user) {
    }

    if (company) {
    }
  }, [user, company]);

  const slug = useMemo(() => {
    if (propSlug) return propSlug;
    const userAny = user as any;
    const companyAny = company as any;

    if (companyAny?.slug) {
      return companyAny.slug;
    }

    if (userAny?.empresa?.slug) {
      return userAny.empresa.slug;
    }

    if (userAny?.company?.slug) {
      return userAny.company.slug;
    }

    if (import.meta.env.DEV) {
      return "kerbrum-agency";
    }

    return "";
  }, [user, company]);

  const organizationName = useMemo(() => {
    const userAny = user as any;
    const companyAny = company as any;

    if (companyAny?.name) {
      return companyAny.name;
    }

    if (userAny?.empresa?.name) {
      return userAny.empresa.name;
    }

    if (userAny?.company?.name) {
      return userAny.company.name;
    }

    if (slug) {
      return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");
    }

    return "Mi Organización";
  }, [user, company, slug]);

  useEffect(() => {
    if (slug) {
      fetchCategories().then(() => {
        fetchProducts();
        fetchRules();
      });
    } else {
      setIsLoading(false);
      setCategoriesLoading(false);
      if (user) {
        setError("Tu cuenta no está asociada a una organización activa.");
      }
    }
  }, [slug, user]);

  useEffect(() => {
    if (slug && user) {
    }
  }, [slug, user, organizationName]);

  const mapApiCategoryToLocal = (
    apiCategory: ApiProductCategory,
  ): LocalProductCategory => {
    return {
      id: apiCategory.id,
      name: apiCategory.name,
      description: apiCategory.description || "",
      color: apiCategory.color || "#3b82f6",
      createdAt: apiCategory.created_at,
      updatedAt: apiCategory.updated_at,
    };
  };

  const mapLocalCategoryToApi = (localCategory: LocalProductCategory): any => {
    return {
      slug: slug,
      name: localCategory.name,
      description: localCategory.description,
      color: localCategory.color,
      is_active: true,
    };
  };

  const fetchCategories = async (): Promise<void> => {
    if (!slug) {
      setError(
        "No se ha identificado la organización. Por favor, inicia sesión.",
      );
      setCategoriesLoading(false);
      setIsLoading(false);
      return;
    }

    try {
      setCategoriesLoading(true);
      setError(null);

      const apiCategories = await productCategoryService.getProductCategories({
        slug,
        is_active: true,
      });

      const mappedCategories = apiCategories.map(mapApiCategoryToLocal);

      const categoriesWithDefault = [
        {
          id: "",
          name: "Sin categoría",
          description: "Productos sin categoría asignada",
          color: "#6b7280",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        ...mappedCategories,
      ];

      setCategories(categoriesWithDefault);
    } catch (err: any) {
      const errorMessage = err.message || "Error al cargar categorías";
      setError(errorMessage);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchRules = async (): Promise<void> => {
    if (!slug) return;
    try {
      const service = productService as any;
      if (service.getRules) {
        const response = await service.getRules({ slug, is_active: true });
        setRules(response.data || []);
      }
    } catch (err) {
      console.error("Error al cargar reglas:", err);
    }
  };

  const createRule = async (ruleData: any): Promise<boolean> => {
    if (!slug) return false;
    try {
      const newRule = await productService.addProductRule(ruleData);

      if (ruleData.product_id) {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id === ruleData.product_id) {
              const ruleToAdd = {
                ...newRule,
                price_modifier: Number(newRule.price_modifier),
              };
              return { ...p, rules: [...(p.rules || []), ruleToAdd as any] };
            }
            return p;
          }),
        );
      }

      toast({
        title: "Regla creada",
        description: "La regla se ha creado correctamente",
      });
      return true;
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateRule = async (id: string, ruleData: any): Promise<boolean> => {
    if (!slug) return false;
    try {
      const updatedRule = await productService.updateProductRule(id, ruleData);

      if (updatedRule.product_id) {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id === updatedRule.product_id) {
              const updatedRules = (p.rules || []).map((r) =>
                r.id === id
                    ? {
                      ...updatedRule,
                      price_modifier: Number(updatedRule.price_modifier),
                    }
                    : r,
              );
              return { ...p, rules: updatedRules as any };
            }
            return p;
          }),
        );
      }

      toast({
        title: "Regla actualizada",
        description: "La regla se ha actualizado correctamente",
      });
      return true;
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteRule = async (
    id: string,
    productId: string,
  ): Promise<boolean> => {
    if (!slug) return false;
    try {
      await productService.deleteProductRule(id);

      setProducts((prev) =>
        prev.map((p) => {
          if (p.id === productId) {
            return {
              ...p,
              rules: (p.rules || []).filter((r) => r.id !== id),
            };
          }
          return p;
        }),
      );

      toast({
        title: "Regla eliminada",
        description: "La regla se ha eliminado correctamente",
      });
      return true;
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const mapApiProductToLocal = (apiProduct: any): Product => {
    if (!apiProduct) {
      throw new Error("Producto API es null o undefined");
    }

    return {
      id: apiProduct.id,
      name: apiProduct.name,
      description: apiProduct.description,
      sku: apiProduct.sku,
      categoryId: apiProduct.category_id || "",
      unitPrice:
        typeof apiProduct.base_price === "string"
          ? parseFloat(apiProduct.base_price)
          : apiProduct.base_price || 0,
      cost: 0,
      stock: 0,
      unit: "Unidad",
      taxRate: 13,
      isActive: apiProduct.is_active,
      saleStartDate: apiProduct.sale_start_date || undefined,
      saleEndDate: apiProduct.sale_end_date || undefined,
      attachments: apiProduct.attachments || [],
      rules: apiProduct.rules || [],
      createdAt: apiProduct.created_at || new Date().toISOString(),
      updatedAt: apiProduct.updated_at || new Date().toISOString(),
    };
  };

  const mapLocalProductToApi = (product: Product): any => {
    return {
      slug: slug,
      name: product.name,
      sku: product.sku || "",
      category_id:
        product.categoryId && product.categoryId !== "no-category"
          ? product.categoryId
          : null,
      base_price: product.unitPrice,
      description: product.description,
      sale_start_date: product.saleStartDate || null,
      sale_end_date: product.saleEndDate || null,
      is_active: product.isActive,
      rule_ids: product.rules?.map((r: any) => r.id) || [],
    };
  };

  const fetchProducts = async (params?: any): Promise<void> => {
    if (!slug) {
      setError(
        "No se ha identificado la organización. Por favor, inicia sesión.",
      );
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const queryParams = {
        slug: slug,
        page: params?.page || currentPage,
        limit: params?.limit || 12,
        search: params?.search || "",
        category_id: params?.category_id,
        is_active: params?.is_active !== undefined ? params.is_active : true,
      };

      console.log("🚀 FRONTEND: Solicitando lista de productos para slug:", slug, queryParams);

      const result = await productService.getProducts(queryParams);

      const mappedProducts = result.data.map(mapApiProductToLocal);

      setProducts(mappedProducts);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
      setCurrentPage(result.pagination.page);
    } catch (err: any) {
      const errorMessage = err.message || "Error al cargar productos";
      console.error("❌ FRONTEND Error en fetchProducts:", err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getProductById = async (id: string): Promise<Product | null> => {
    if (!slug) {
      return null;
    }

    try {
      setIsLoading(true);
      const apiProduct = await productService.getProductById(id, slug);

      if (!apiProduct) {
        return null;
      }

      const mappedProduct = mapApiProductToLocal(apiProduct);
      return mappedProduct;
    } catch (err: any) {
      setError(err.message || "Error al obtener el producto");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const createProduct = async (productData: any): Promise<Product | null> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return null;
    }

    try {
      setIsLoading(true);

      const apiData = mapLocalProductToApi(productData);
      console.log("🚀 FRONTEND: Iniciando proceso de creación de producto:", apiData);
      
      const apiProduct = await productService.createProduct(apiData);
      console.log("✅ FRONTEND: Producto creado exitosamente en API:", apiProduct);
      
      const newProduct = mapApiProductToLocal(apiProduct);

      setProducts((prev) => [...prev, newProduct]);
      setTotalItems((prev) => prev + 1);

      toast({
        title: "Producto creado",
        description: `El producto "${newProduct.name}" ha sido creado correctamente`,
      });

      return newProduct;
    } catch (err: any) {
      const errorMessage = err.message || "Error al crear el producto";
      console.error("❌ FRONTEND Error en createProduct:", err);
      setError(errorMessage);

      toast({
        title: "Error",
        description:
          "No se pudo crear el producto. Verifica los datos e intenta nuevamente.",
        variant: "destructive",
      });

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProduct = async (
    id: string,
    productData: any,
  ): Promise<Product | null> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return null;
    }

    try {
      setIsLoading(true);

      const apiData = mapLocalProductToApi(productData);
      const apiProduct = await productService.updateProduct(id, apiData);
      const updatedProduct = mapApiProductToLocal(apiProduct);

      setProducts((prev) =>
        prev.map((p) => (p.id === id ? updatedProduct : p)),
      );

      toast({
        title: "Producto actualizado",
        description: `El producto "${updatedProduct.name}" ha sido actualizado correctamente`,
      });

      return updatedProduct;
    } catch (err: any) {
      const errorMessage = err.message || "Error al actualizar el producto";
      setError(errorMessage);

      toast({
        title: "Error",
        description:
          "No se pudo actualizar el producto. Verifica los datos e intenta nuevamente.",
        variant: "destructive",
      });

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProduct = async (id: string): Promise<boolean> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);

      const success = await productService.deleteProduct(id, slug);

      if (success) {
        setProducts((prev) => prev.filter((p) => p.id !== id));
        setTotalItems((prev) => prev - 1);

        toast({
          title: "Producto eliminado",
          description: "El producto ha sido eliminado correctamente",
        });
      }

      return success;
    } catch (err: any) {
      const errorMessage = err.message || "Error al eliminar the product";
      setError(errorMessage);

      toast({
        title: "Error",
        description: "No se pudo eliminar el producto. Intenta nuevamente.",
        variant: "destructive",
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const createCategory = async (
    categoryData: any,
  ): Promise<LocalProductCategory | null> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return null;
    }

    try {
      setCategoriesLoading(true);

      const apiData = mapLocalCategoryToApi(categoryData);
      const apiCategory =
        await productCategoryService.createProductCategory(apiData);
      const newCategory = mapApiCategoryToLocal(apiCategory);

      setCategories((prev) => {
        const existing = prev.find((cat) => cat.id === newCategory.id);
        if (existing) {
          return prev.map((cat) =>
            cat.id === newCategory.id ? newCategory : cat,
          );
        }
        const [noCategory, ...rest] = prev;
        return [noCategory, newCategory, ...rest];
      });

      toast({
        title: "Categoría creada",
        description: `La categoría "${newCategory.name}" ha sido creada correctamente`,
      });

      return newCategory;
    } catch (err: any) {
      const errorMessage = err.message || "Error al crear la categoría";
      setError(errorMessage);

      toast({
        title: "Error",
        description:
          "No se pudo crear la categoría. Verifica los datos e intenta nuevamente.",
        variant: "destructive",
      });

      return null;
    } finally {
      setCategoriesLoading(false);
    }
  };

  const updateCategory = async (
    id: string,
    categoryData: any,
  ): Promise<LocalProductCategory | null> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return null;
    }

    try {
      setCategoriesLoading(true);

      const apiData = mapLocalCategoryToApi(categoryData);
      const apiCategory = await productCategoryService.updateProductCategory(
        id,
        apiData,
      );
      const updatedCategory = mapApiCategoryToLocal(apiCategory);

      setCategories((prev) =>
        prev.map((cat) => (cat.id === id ? updatedCategory : cat)),
      );

      toast({
        title: "Categoría actualizada",
        description: `La categoría "${updatedCategory.name}" ha sido actualizada correctamente`,
      });

      return updatedCategory;
    } catch (err: any) {
      const errorMessage = err.message || "Error al actualizar la categoría";
      setError(errorMessage);

      toast({
        title: "Error",
        description:
          "No se pudo actualizar la categoría. Verifica los datos e intenta nuevamente.",
        variant: "destructive",
      });

      return null;
    } finally {
      setCategoriesLoading(false);
    }
  };

  const deleteCategory = async (id: string): Promise<boolean> => {
    if (!slug) {
      toast({
        title: "Error",
        description:
          "No se ha identificado la organización. Por favor, inicia sesión.",
        variant: "destructive",
      });
      return false;
    }

    try {
      setCategoriesLoading(true);

      const success = await productCategoryService.deleteProductCategory(
        id,
        slug,
      );

      if (success) {
        setCategories((prev) => prev.filter((cat) => cat.id !== id));

        toast({
          title: "Categoría eliminada",
          description: "La categoría ha sido eliminada correctamente",
        });
      }

      return success;
    } catch (err: any) {
      const errorMessage = err.message || "Error al eliminar la categoría";
      setError(errorMessage);

      toast({
        title: "Error",
        description: "No se pudo eliminar la categoría. Intenta nuevamente.",
        variant: "destructive",
      });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue = useMemo(() => ({
    products,
    categories,
    rules,
    isLoading,
    categoriesLoading,
    error,
    currentPage,
    totalPages,
    totalItems,
    slug,
    organizationName,
    fetchProducts,
    fetchCategories,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    createCategory,
    updateCategory,
    deleteCategory,
    setCurrentPage,
  }), [
    products,
    categories,
    rules,
    isLoading,
    categoriesLoading,
    error,
    currentPage,
    totalPages,
    totalItems,
    slug,
    organizationName,
  ]);

  return (
    <ProductsContext.Provider value={contextValue}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error("useProducts must be used within a ProductsProvider");
  }
  return context;
}
